import { useId, useState } from "react";

export interface BrandLogoProps {
  size?: number;
  className?: string;
}

/**
 * Renders the real Jaiho91 logo (apps/web/public/brand/jaiho-social-logo-transparent.png)
 * when present, falling back to an inline SVG monogram badge when it isn't —
 * so the app never shows a broken-image icon before real brand assets are
 * supplied. See apps/web/public/brand/README.md.
 *
 * The fallback is an SVG (not a CSS `background-clip: text` wordmark) on
 * purpose: gradient clip-text is unreliable in Telegram's in-app WebView
 * (some builds fail to clip the gradient to the glyphs and instead paint
 * the whole background box, which read as a solid bar overlapping the
 * adjacent "Jaiho91 Social" label). An SVG gradient fill renders
 * consistently everywhere and, being a square icon rather than a second
 * copy of the wordmark, doesn't duplicate text next to it either.
 */
export function BrandLogo({ size = 40, className }: BrandLogoProps) {
  const [failed, setFailed] = useState(false);
  const gradientId = useId();

  if (failed) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        className={className}
        role="img"
        aria-label="Jaiho91 Social"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#CF0563" />
            <stop offset="1" stopColor="#F510D7" />
          </linearGradient>
        </defs>
        <rect width="64" height="64" rx="16" fill="#05090C" />
        <rect x="4" y="4" width="56" height="56" rx="13" fill={`url(#${gradientId})`} opacity="0.18" />
        <text
          x="32"
          y="43"
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
          fontWeight="800"
          fontSize="34"
          fill={`url(#${gradientId})`}
        >
          J
        </text>
      </svg>
    );
  }

  return (
    <img
      src="/brand/jaiho-social-logo-transparent.png"
      alt="Jaiho91 Social"
      width={size}
      height={size}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}
